import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import {
  Wifi, WifiOff, AlertTriangle, CheckCircle2, Clock, RefreshCw,
  Brain, MessageSquare, Volume2, CreditCard, Mail, Database,
  Activity, ShieldAlert, Bell, Loader2, ChevronDown, ChevronUp,
  Zap, Send, DollarSign, Users, TrendingUp
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export function WatchtowerCard({ headers }) {
  const [wt, setWt] = useState(null);
  const [sending, setSending] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/watchtower`, { headers });
      setWt(data.watchtower);
    } catch (e) { console.error(e); }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/watchtower/send`, {}, { headers });
      toast.success(`Compliance review email sent (${data.quarter})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
  return (
    <div data-testid="watchtower-card" className="bg-card border border-border/50 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
          <ShieldAlert className="w-4.5 h-4.5 text-primary" style={{ width: 18, height: 18 }} />
        </div>
        <div>
          <p className="text-sm font-semibold">Regulation Watchtower</p>
          <p className="text-xs text-muted-foreground">
            Quarterly compliance-review email · Last sent: <span className="font-medium">{fmt(wt?.last_sent)}</span> · Next due:{" "}
            <span className="font-medium" data-testid="watchtower-next-due">{fmt(wt?.next_due)}</span>
          </p>
        </div>
      </div>
      <button
        data-testid="watchtower-send-btn"
        onClick={sendNow}
        disabled={sending}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        Send Review Now
      </button>
    </div>
  );
}

export function RevenueCard({ headers }) {
  const [rev, setRev] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/revenue`, { headers });
      setRev(data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!rev) return null;
  const stats = [
    { icon: DollarSign, label: `Revenue — ${rev.month}`, value: `$${rev.month_revenue.toFixed(2)}`, sub: `${rev.month_payments} payment${rev.month_payments === 1 ? "" : "s"}`, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    { icon: TrendingUp, label: "MRR Estimate", value: `$${rev.mrr_estimate.toFixed(2)}`, sub: `All-time: $${rev.all_time_revenue.toFixed(2)}`, color: "text-primary", bg: "bg-primary/10" },
    { icon: Users, label: "Active Subscribers", value: rev.active_subscribers, sub: Object.entries(rev.by_tier).map(([k, v]) => `${rev.tier_names?.[k] || k}: ${v}`).join(" · ") || "None yet", color: "text-amber-500", bg: "bg-amber-500/10" },
    { icon: Zap, label: "Active Add-ons", value: rev.active_addons, sub: `$${rev.month_addon_revenue.toFixed(2)} add-on revenue this month`, color: "text-violet-500", bg: "bg-violet-500/10" },
  ];
  return (
    <div data-testid="revenue-card" className="bg-card border border-border/50 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="w-4 h-4 text-emerald-500" strokeWidth={1.5} />
        <p className="text-sm font-semibold">Revenue & Subscribers</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="p-3 rounded-xl border border-border/40 bg-muted/20">
            <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center mb-2", s.bg)}>
              <s.icon className={cn("w-3.5 h-3.5", s.color)} strokeWidth={1.5} />
            </div>
            <p className="text-lg font-semibold leading-none" data-testid={`revenue-stat-${s.label.split(" ")[0].toLowerCase().replace("—", "")}`}>{s.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{s.label}</p>
            <p className="text-[10px] text-muted-foreground/70 mt-0.5 truncate" title={s.sub}>{s.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ModuleGateCard({ headers }) {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/features`);
      setEnabled(data.psychologist_enabled);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/features/psychologist`, { enabled: !enabled }, { headers });
      setEnabled(data.psychologist_enabled);
      toast.success(data.psychologist_enabled ? "The Psychologist is now LIVE" : "The Psychologist set aside — shown as Coming Soon");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="module-gate-card" className="bg-card border border-border/50 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
          <Brain className="text-blue-500" style={{ width: 18, height: 18 }} />
        </div>
        <div>
          <p className="text-sm font-semibold">The Psychologist — Regulatory Gate</p>
          <p className="text-xs text-muted-foreground" data-testid="module-gate-status">
            {enabled === null ? "Loading…" : enabled
              ? "LIVE — visible and usable by subscribers"
              : "Set aside — shown as Coming Soon until statutory registrations complete"}
          </p>
        </div>
      </div>
      <button
        data-testid="module-gate-toggle"
        onClick={toggle}
        disabled={saving || enabled === null}
        className={cn(
          "inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-50 transition-colors",
          enabled ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400" : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
        )}
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : enabled ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
        {enabled ? "Live — click to set aside" : "Coming Soon — click to go live"}
      </button>
    </div>
  );
}

const STATUS_MAP = {
  operational: { label: "Operational", color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500", dot: "bg-emerald-500", badge: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800" },
  degraded: { label: "Degraded", color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500", dot: "bg-amber-500", badge: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800" },
  down: { label: "Down", color: "text-red-600 dark:text-red-400", bg: "bg-red-500", dot: "bg-red-500", badge: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800" },
  auth_error: { label: "Auth Error", color: "text-red-600 dark:text-red-400", bg: "bg-red-500", dot: "bg-red-500", badge: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800" },
  rate_limited: { label: "Rate Limited", color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500", dot: "bg-amber-500", badge: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800" },
  not_configured: { label: "Not Configured", color: "text-muted-foreground", bg: "bg-muted", dot: "bg-gray-400", badge: "bg-muted text-muted-foreground border-border" },
  unknown: { label: "Unknown", color: "text-muted-foreground", bg: "bg-muted", dot: "bg-gray-400", badge: "bg-muted text-muted-foreground border-border" },
};

const CATEGORY_ICONS = {
  "AI / Chat": Brain,
  "Voice": Volume2,
  "Payments": CreditCard,
  "Email": Mail,
  "Database": Database,
};

function MiniSparkline({ data, height = 28 }) {
  if (!data || data.length === 0) return null;
  const w = 168;
  const h = height;
  const barW = Math.floor(w / data.length) - 1;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="overflow-visible">
      {data.map((point, i) => {
        const x = i * (barW + 1);
        let fill;
        if (point.uptime_pct === -1) fill = "rgba(156,163,175,0.15)"; // no data
        else if (point.uptime_pct === 100) fill = "rgb(34,197,94)";
        else if (point.uptime_pct >= 80) fill = "rgb(234,179,8)";
        else fill = "rgb(239,68,68)";
        return (
          <rect
            key={i}
            x={x}
            y={0}
            width={barW}
            height={h}
            rx={1.5}
            fill={fill}
            className="transition-all"
          >
            <title>
              {point.uptime_pct === -1
                ? `Hour ${point.hour}: No data`
                : `Hour ${point.hour}: ${point.uptime_pct}% uptime, ${point.avg_latency_ms}ms avg`}
            </title>
          </rect>
        );
      })}
    </svg>
  );
}

function ProviderCard({ provider }) {
  const [expanded, setExpanded] = useState(false);
  const statusInfo = STATUS_MAP[provider.status] || STATUS_MAP.unknown;
  const CatIcon = CATEGORY_ICONS[provider.category] || Activity;

  return (
    <div
      data-testid={`provider-card-${provider.id}`}
      className="bg-card border border-border/50 rounded-2xl overflow-hidden transition-all hover:border-border"
    >
      <div
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Top row: icon + name + status */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-muted/50 flex items-center justify-center">
              <CatIcon className="w-4 h-4 text-foreground/70" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight">{provider.name}</p>
              <p className="text-[10px] text-muted-foreground/60">{provider.category}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn("inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-full border", statusInfo.badge)}>
              <span className={cn("w-1.5 h-1.5 rounded-full", statusInfo.dot, provider.status === "operational" && "animate-pulse")} />
              {statusInfo.label}
            </span>
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/40" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/40" />}
          </div>
        </div>

        {/* Sparkline: 24h uptime */}
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-muted-foreground/50">24h Uptime</span>
            <span className={cn("text-[10px] font-bold", provider.uptime_24h >= 99 ? "text-emerald-600" : provider.uptime_24h >= 90 ? "text-amber-600" : "text-red-600")}>
              {provider.uptime_24h}%
            </span>
          </div>
          <MiniSparkline data={provider.sparkline} />
          <div className="flex items-center justify-between mt-0.5">
            <span className="text-[9px] text-muted-foreground/30">24h ago</span>
            <span className="text-[9px] text-muted-foreground/30">now</span>
          </div>
        </div>

        {/* Quick stats row */}
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground/60">
          {provider.latency_ms > 0 && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {provider.latency_ms}ms
            </span>
          )}
          {provider.auth_errors_24h > 0 && (
            <span className="flex items-center gap-1 text-red-500">
              <ShieldAlert className="w-3 h-3" /> {provider.auth_errors_24h} auth errors
            </span>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-border/40 px-4 py-3 bg-muted/10 animate-in fade-in slide-in-from-top-1 duration-200 space-y-2">
          <div className="flex items-start gap-2">
            <span className="text-[10px] font-semibold text-muted-foreground/60 w-16 flex-shrink-0">Status</span>
            <p className="text-xs">{provider.detail}</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-[10px] font-semibold text-muted-foreground/60 w-16 flex-shrink-0">Role</span>
            <p className="text-xs text-muted-foreground">{provider.description}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function ProvidersPanel() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/providers`, { headers });
      setData(resp.data);
    } catch (err) {
      toast.error("Failed to load provider status");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchProviders(); }, []);

  const handleSendAlerts = async () => {
    setSendingAlerts(true);
    try {
      const resp = await axios.post(`${API}/admin/providers/check-alerts`, {}, { headers });
      if (resp.data.alerts_sent > 0) {
        toast.success(`Sent ${resp.data.alerts_sent} alert(s) to admin`);
      } else {
        toast.info("All providers healthy — no alerts to send");
      }
    } catch {
      toast.error("Failed to send alerts");
    } finally {
      setSendingAlerts(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div data-testid="providers-panel" className="space-y-5">
      {/* Summary Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card border border-border/50">
          <Wifi className="w-4 h-4 text-emerald-500" />
          <span className="text-sm font-semibold">{data.operational_count}/{data.total_providers}</span>
          <span className="text-xs text-muted-foreground">Operational</span>
        </div>
        {data.degraded_count > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">{data.degraded_count}</span>
            <span className="text-xs text-amber-600/70 dark:text-amber-400/60">Degraded</span>
          </div>
        )}
        {data.down_count > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
            <WifiOff className="w-4 h-4 text-red-500" />
            <span className="text-sm font-semibold text-red-700 dark:text-red-400">{data.down_count}</span>
            <span className="text-xs text-red-600/70 dark:text-red-400/60">Down</span>
          </div>
        )}
        <div className="flex-1" />
        <button
          data-testid="send-alerts-btn"
          onClick={handleSendAlerts}
          disabled={sendingAlerts}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-border/50 hover:bg-muted/50 transition-colors disabled:opacity-50"
        >
          {sendingAlerts ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send Alert Email
        </button>
        <button
          data-testid="refresh-providers"
          onClick={fetchProviders}
          disabled={loading}
          className="p-2 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
        >
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
        </button>
      </div>

      {/* Regulation Watchtower */}
      <WatchtowerCard headers={headers} />

      {/* Module regulatory gate */}
      <ModuleGateCard headers={headers} />

      {/* Revenue & subscribers */}
      <RevenueCard headers={headers} />

      {/* Credit Runway (always visible) */}
      {data.credit_runway?.length > 0 && (
        <div data-testid="credit-runway" className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {data.credit_runway.map((c) => (
            <div key={c.provider_id} data-testid={`runway-${c.provider_id}`} className="bg-card border border-border/50 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-primary" /> {c.name}
                </span>
                <span className={cn("text-xs font-bold", c.remaining_pct > 40 ? "text-emerald-600" : c.remaining_pct > 15 ? "text-amber-600" : "text-red-600")}>
                  {c.remaining_pct}% left
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted/60 overflow-hidden">
                <div
                  className={cn("h-full rounded-full transition-all", c.remaining_pct > 40 ? "bg-emerald-500" : c.remaining_pct > 15 ? "bg-amber-500" : "bg-red-500")}
                  style={{ width: `${c.remaining_pct}%` }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground/60 mt-1.5">
                ${c.used_usd.toFixed(2)} used of ${c.cap_usd.toFixed(2)} monthly allocation
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Credit & Auth Alerts */}
      {data.alerts?.length > 0 && (
        <div data-testid="provider-alerts" className="space-y-2">
          {data.alerts.map((alert, i) => (
            <div
              key={i}
              data-testid={`provider-alert-${i}`}
              className={cn(
                "flex items-center gap-2.5 px-4 py-3 rounded-xl border",
                alert.level === "critical"
                  ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40"
                  : "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40"
              )}
            >
              {alert.level === "critical"
                ? <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" strokeWidth={2} />
                : <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" strokeWidth={2} />
              }
              <div className="flex-1 min-w-0">
                <p className={cn("text-xs font-semibold", alert.level === "critical" ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400")}>
                  {alert.provider_name}
                </p>
                <p className={cn("text-[11px]", alert.level === "critical" ? "text-red-600/70 dark:text-red-400/60" : "text-amber-600/70 dark:text-amber-400/60")}>
                  {alert.message}
                </p>
              </div>
              {alert.type === "low_credit" && alert.remaining_pct !== undefined && (
                <span className="text-xs font-bold text-red-600 dark:text-red-400">{alert.remaining_pct}%</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Provider Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {data.providers.map(provider => (
          <ProviderCard key={provider.id} provider={provider} />
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-[10px] text-muted-foreground/50">
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-emerald-500" /> 100% uptime</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-amber-500" /> Partial</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-red-500" /> Down</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm" style={{ background: "rgba(156,163,175,0.15)" }} /> No data</span>
      </div>

      {/* Integration Setup Guide */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <div className="bg-card border border-border/50 rounded-2xl p-4" data-testid="webhook-setup-card">
          <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <CreditCard className="w-3.5 h-3.5 text-primary" /> Stripe Webhook Setup
          </h4>
          <ol className="text-[11px] text-muted-foreground space-y-1.5 list-decimal list-inside">
            <li>Go to <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="text-primary hover:underline">Stripe Dashboard → Webhooks</a></li>
            <li>Click "Add endpoint"</li>
            <li>Set URL: <code className="px-1.5 py-0.5 bg-muted/50 rounded text-[10px] break-all">{window.location.origin}/api/webhook/stripe</code></li>
            <li>Select events: <code className="text-[10px]">checkout.session.completed</code>, <code className="text-[10px]">charge.refunded</code></li>
            <li>Copy the signing secret to your backend .env</li>
          </ol>
          <p className="text-[10px] text-emerald-500 mt-2">Webhook live — signing secret configured, signature verification active</p>
        </div>
        <div className="bg-card border border-border/50 rounded-2xl p-4" data-testid="domain-verify-card">
          <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5 text-primary" /> Resend Domain Verification
          </h4>
          <ol className="text-[11px] text-muted-foreground space-y-1.5 list-decimal list-inside">
            <li>Go to <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-primary hover:underline">Resend → Domains</a></li>
            <li>Click "Add Domain" and enter your sending domain</li>
            <li>Add the DNS records (SPF, DKIM, DMARC) to your domain registrar</li>
            <li>Wait for verification (usually 5-10 min)</li>
            <li>Update <code className="text-[10px]">SENDER_EMAIL</code> in backend .env to your verified domain</li>
          </ol>
          <p className="text-[10px] text-amber-500 mt-2">Sender: admin@omnimodules.com — DKIM record pending verification at your DNS provider</p>
        </div>
      </div>
    </div>
  );
}
